import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 * 
 * Catches React component errors and prevents blank screens.
 * Shows a friendly error message with option to reload.
 * Logs error details in development mode only.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // CRITICAL: Always log full error details in DEV for debugging
    if (import.meta.env.DEV) {
      console.error('[ERROR BOUNDARY] ===== CAUGHT ERROR =====');
      console.error('[ERROR BOUNDARY] Error name:', error.name);
      console.error('[ERROR BOUNDARY] Error message:', error.message);
      console.error('[ERROR BOUNDARY] Error stack:', error.stack);
      console.error('[ERROR BOUNDARY] Component stack:', errorInfo.componentStack);
      console.error('[ERROR BOUNDARY] Full error object:', error);
      console.error('[ERROR BOUNDARY] Full error info:', errorInfo);
      console.error('[ERROR BOUNDARY] =========================');
    }
    
    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    // Reset error state and reload the page
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center gap-6 p-8">
          <div className="bg-slate-900 border-2 border-red-500/50 rounded-[3rem] p-10 max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="p-4 bg-red-500/10 rounded-full">
                <AlertTriangle className="text-red-500" size={48} />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-black text-white uppercase tracking-tighter">
                Qualcosa è andato storto
              </h1>
              <p className="text-slate-400 text-sm leading-relaxed">
                Si è verificato un errore imprevisto. Puoi provare a ricaricare la pagina.
              </p>
            </div>

            {/* Error details in development only */}
            {import.meta.env.DEV && this.state.error && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-left space-y-3">
                <div>
                  <p className="text-xs font-black text-red-400 uppercase mb-1">Error Name:</p>
                  <p className="text-xs text-slate-300 font-mono">{this.state.error.name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs font-black text-red-400 uppercase mb-1">Error Message:</p>
                  <p className="text-xs text-slate-300 font-mono break-all">{this.state.error.message || 'No message'}</p>
                </div>
                {this.state.error.stack && (
                  <details className="mt-2">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400 mb-1">
                      Stack Trace
                    </summary>
                    <pre className="text-[10px] text-slate-400 mt-2 overflow-auto max-h-60 font-mono whitespace-pre-wrap">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
                {this.state.errorInfo?.componentStack && (
                  <details className="mt-2">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400 mb-1">
                      Component Stack
                    </summary>
                    <pre className="text-[10px] text-slate-400 mt-2 overflow-auto max-h-60 font-mono whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <button
              onClick={this.handleReload}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase tracking-widest rounded-2xl transition-all shadow-lg shadow-emerald-500/20"
            >
              <RefreshCw size={18} />
              Ricarica
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

